import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { ROUTE_TYPE_META, RouteBadge } from "@/lib/routeTypes";
import { Download, Trash2, Pencil, ChevronDown, ChevronRight, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const TABS = ["all", "small", "big", "pickup", "walmart", "split"];

const ROUTE_TYPES = ["small", "big", "pickup", "walmart", "split", "other"];

function EditRouteDialog({ entry, onDone }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    route_code: entry.route_code || "",
    driver_name: entry.driver_name || "",
    van_code: entry.van_code || "",
    date: (entry.date || "").slice(0, 10),
    wave: entry.wave || "",
    route_type: entry.route_type || "other",
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await api.patch(`/assignments/${entry.id}`, form);
      toast.success("Route updated");
      qc.invalidateQueries();
      setOpen(false);
      onDone && onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`edit-route-${entry.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-[#2563EB] hover:bg-blue-50 rounded-sm">
          <Pencil className="w-3.5 h-3.5" />
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Edit route entry</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {[
            ["route_code", "Route code"],
            ["driver_name", "Driver"],
            ["van_code", "Van"],
            ["date", "Date"],
            ["wave", "Wave"],
          ].map(([k, label]) => (
            <label key={k} className="block">
              <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</span>
              <input
                type={k === "date" ? "date" : "text"}
                value={form[k]}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`edit-route-${k}`}
                className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data text-sm"
              />
            </label>
          ))}
          <label className="block">
            <span className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Route type</span>
            <select
              value={form.route_type}
              onChange={(e) => setForm((f) => ({ ...f, route_type: e.target.value }))}
              data-testid="edit-route-type"
              className="mt-1 w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
            >
              {ROUTE_TYPES.map((t) => (
                <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
              ))}
            </select>
            <span className="text-xs text-slate-500 mt-1 block">Choosing a type here overrides the auto-rule for this entry only.</span>
          </label>
        </div>
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="edit-route-save" className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteRouteButton({ entry }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  async function doDelete() {
    setBusy(true);
    try {
      await api.delete(`/assignments/${entry.id}`);
      toast.success("Deleted");
      qc.invalidateQueries();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button data-testid={`delete-route-${entry.id}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this route entry?</AlertDialogTitle>
          <AlertDialogDescription>{entry.route_code} · {entry.driver_name} · {entry.van_code} on {entry.date?.slice(0, 10)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={busy} onClick={doDelete} className="bg-red-600 hover:bg-red-700">
            {busy ? "Deleting..." : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function DownloadRouteButton({ routeCode }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/export/route/${encodeURIComponent(routeCode)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `route_${routeCode}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${routeCode}`);
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  }
  return (
    <button onClick={go} disabled={busy} data-testid={`download-route-${routeCode}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-[#2563EB] hover:bg-blue-50 rounded-sm disabled:opacity-50">
      <Download className="w-3.5 h-3.5" />
    </button>
  );
}

function DownloadEntryButton({ entryId, routeCode }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/export/entry/${encodeURIComponent(entryId)}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${routeCode || "entry"}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Downloaded");
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  }
  return (
    <button onClick={go} disabled={busy} data-testid={`download-entry-${entryId}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E2E2E8] hover:bg-blue-50 hover:text-[#2563EB] rounded-md disabled:opacity-50">
      <Download className="w-3.5 h-3.5" /> xlsx
    </button>
  );
}

function DayCard({ day }) {
  const [open, setOpen] = useState(true);
  const [dlBusy, setDlBusy] = useState(false);
  const total = day.entries.length;

  async function downloadDay(e) {
    e.stopPropagation();
    setDlBusy(true);
    try {
      const res = await fetch(`${API}/export/day?date=${day.day}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `routes_${day.day}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${day.weekday}`);
    } catch (err) {
      toast.error(err.message);
    } finally { setDlBusy(false); }
  }

  return (
    <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden" data-testid={`day-${day.day}`}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E2E8] hover:bg-slate-50 transition-colors duration-150">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left flex-1">
          {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
          <span className="font-heading font-medium">{day.weekday}</span>
          <span className="text-xs text-slate-500 font-mono-data">{day.day}</span>
        </button>
        <div className="flex items-center gap-3 text-xs">
          {["small","big","pickup","walmart","split"].map((k) => (
            (day.counts[k] || 0) > 0 && (
              <span key={k} className="font-mono-data" style={{ color: ROUTE_TYPE_META[k].text }}>
                {ROUTE_TYPE_META[k].label.slice(0, 1)}:{day.counts[k]}
              </span>
            )
          ))}
          <span className="font-mono-data font-semibold text-[#2563EB]">{total} entries · {day.unique_routes} routes</span>
          {total > 0 && (
            <button
              onClick={downloadDay}
              disabled={dlBusy}
              data-testid={`download-day-${day.day}`}
              className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-[#E2E2E8] hover:bg-blue-50 hover:text-[#2563EB] rounded-md ml-2 disabled:opacity-50"
            >
              <Download className="w-3.5 h-3.5" /> Day
            </button>
          )}
        </div>
      </div>
      {open && total > 0 && (
        <div>
          <div className="grid grid-cols-12 px-5 py-2 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
            <div className="col-span-2">Route</div>
            <div className="col-span-3">Driver</div>
            <div className="col-span-2">Van</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-1">Wave</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {day.entries.map((e) => (
            <div key={e.id} className="grid grid-cols-12 px-5 py-2.5 border-b border-[#E2E2E8] last:border-b-0 items-center hover:bg-slate-50 transition-colors duration-150">
              <div className="col-span-2 font-mono-data text-sm">{e.route_code}</div>
              <div className="col-span-3 text-sm">{e.driver_name}</div>
              <div className="col-span-2 font-mono-data text-sm text-slate-600">{e.van_code}</div>
              <div className="col-span-2"><RouteBadge type={e.route_type} /></div>
              <div className="col-span-1 font-mono-data text-xs text-slate-500">{e.wave || "—"}</div>
              <div className="col-span-2 flex items-center justify-end gap-1">
                <DownloadEntryButton entryId={e.id} routeCode={e.route_code} />
                <EditRouteDialog entry={e} />
                <DeleteRouteButton entry={e} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Routes() {
  const qc = useQueryClient();
  const { data: weeksData } = useQuery({
    queryKey: ["weeks"],
    queryFn: async () => (await api.get("/weeks")).data,
  });
  const weeks = useMemo(() => weeksData?.weeks || [], [weeksData]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const initialTab = TABS.includes(tabParam) ? tabParam : "all";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && TABS.includes(t) && t !== tab) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const changeTab = (key) => { setTab(key); setSearchParams({ tab: key, ...(selectedWeek ? { week: selectedWeek } : {}) }); };

  const [selectedWeek, setSelectedWeek] = useState(searchParams.get("week") || (weeks[0]?.start || ""));
  useEffect(() => {
    if (!selectedWeek && weeks.length > 0) setSelectedWeek(weeks[0].start);
  }, [weeks, selectedWeek]);

  const { data: weekDetail } = useQuery({
    queryKey: ["week-detail", selectedWeek],
    queryFn: async () => (await api.get(`/week?start=${selectedWeek}`)).data,
    enabled: !!selectedWeek,
  });

  const filteredDays = useMemo(() => {
    if (!weekDetail) return [];
    if (tab === "all") return weekDetail.days;
    return weekDetail.days.map((d) => ({
      ...d,
      entries: d.entries.filter((e) => e.route_type === tab),
      unique_routes: new Set(d.entries.filter((e) => e.route_type === tab).map((e) => (e.route_code || "").toLowerCase())).size,
    }));
  }, [weekDetail, tab]);

  const [dlLoading, setDlLoading] = useState(false);
  const [dlTypeBusy, setDlTypeBusy] = useState(false);

  async function downloadType() {
    setDlTypeBusy(true);
    try {
      const url = `${API}/export/type?type=${tab}${selectedWeek ? `&start=${selectedWeek}` : ""}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${tab}_routes_${selectedWeek || "current"}.xlsx`;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toast.success(`Downloaded ${tab}`);
    } catch (e) {
      toast.error(e.message);
    } finally { setDlTypeBusy(false); }
  }

  async function downloadWeek() {
    setDlLoading(true);
    try {
      const url = selectedWeek ? `${API}/export?start=${selectedWeek}` : `${API}/export`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `routes_week_${selectedWeek || "current"}.xlsx`;
      a.click();
      URL.revokeObjectURL(objectUrl);
      toast.success("Week downloaded");
    } catch (e) {
      toast.error(e.message || "Failed");
    } finally { setDlLoading(false); }
  }

  const totalUnique = filteredDays.reduce((s, d) => s + d.unique_routes, 0);

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Weekly Log</div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">Routes</h1>
          <p className="text-sm text-slate-500 mt-1">Routes grouped by day. Switch weeks to browse the last 60 days.</p>
        </div>
        <button
          onClick={downloadWeek}
          disabled={dlLoading}
          data-testid="download-week-button"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60"
        >
          <Download className="w-4 h-4" /> {dlLoading ? "Downloading..." : "Download Week"}
        </button>
      </div>

      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <CalendarDays className="w-4 h-4 text-slate-400" />
        <select
          value={selectedWeek}
          onChange={(e) => { setSelectedWeek(e.target.value); setSearchParams({ tab, week: e.target.value }); }}
          data-testid="week-selector"
          className="px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono-data bg-white"
        >
          {weeks.length === 0 && <option value="">No data</option>}
          {weeks.map((w) => (
            <option key={w.start} value={w.start}>Week of {w.start} · {w.total_routes} routes</option>
          ))}
        </select>
        <span className="text-xs text-slate-500 ml-auto font-mono-data">{totalUnique} unique routes shown</span>
      </div>

      <div className="mt-6 flex items-center gap-2 border-b border-[#E2E2E8] overflow-x-auto no-scrollbar">
        <div className="flex gap-1 flex-1">
          {TABS.map((key) => {
            const meta = key === "all" ? { label: "All" } : ROUTE_TYPE_META[key];
            const active = tab === key;
            return (
              <button
                key={key}
                data-testid={`tab-${key}`}
                onClick={() => changeTab(key)}
                className={`flex-shrink-0 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-150 ${
                  active ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"
                }`}
              >
                {meta.label}
              </button>
            );
          })}
        </div>
        {selectedWeek && (
          <button
            onClick={downloadType}
            disabled={dlTypeBusy}
            data-testid={`download-type-${tab}`}
            className="flex-shrink-0 inline-flex items-center gap-1 px-3 py-1.5 mb-1 text-xs border border-[#E2E2E8] hover:bg-blue-50 hover:text-[#2563EB] rounded-md disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> {dlTypeBusy ? "Downloading..." : `Download ${tab === "all" ? "All" : (ROUTE_TYPE_META[tab]?.label || tab)}`}
          </button>
        )}
      </div>

      <div className="mt-6 space-y-3">
        {!weekDetail && <div className="p-6 text-sm text-slate-500">Loading…</div>}
        {weekDetail && filteredDays.map((d) => <DayCard key={d.day} day={d} />)}
      </div>
    </div>
  );
}
